/* leaflet-stack.js — load Leaflet + the real go.lacak.io plugin stack.
 * The deployed app renders the map on Leaflet 1.9.4 (framework) with Google Maps supplying
 * the tiles via GoogleMutant, PruneCluster for the canvas donut clustering, leaflet-hotline
 * for the speed-coloured history track, and leaflet-ant-path for animated routes. These
 * plugins register onto a GLOBAL `L`, so we expose it before importing them, and GoogleMutant
 * needs `window.google.maps` present first.
 */

import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { loadGoogleMaps } from './gmaps-loader.js'

// Plugins that expect `window.L` (PruneCluster, hotline) must see it before they load.
if (typeof window !== 'undefined' && !window.L) window.L = L

let ready = null

// Load Google Maps (for GoogleMutant tiles) then all the Leaflet plugins, once.
export function loadLeafletStack() {
  if (ready) return ready
  ready = (async () => {
    await loadGoogleMaps() // GoogleMutant wraps window.google.maps
    // Explicit file paths — these old plugins have quirky package.json entries that Vite's
    // package-entry resolution can't handle (googlemutant has only `module`, hotline ships
    // UMD source, prunecluster is TS-compiled and attaches to the global L).
    await import('leaflet.gridlayer.googlemutant/dist/Leaflet.GoogleMutant.js')
    await import('leaflet-ant-path')
    // hotline's UMD exports a plugin(L) function under ESM interop — call it to register L.hotline.
    const hot = await import('leaflet-hotline/src/leaflet.hotline.js')
    const hotFn = hot.default || hot
    if (typeof hotFn === 'function' && !L.hotline) hotFn(L)
    // PruneCluster declares top-level `var PruneCluster(ForLeaflet)` with no exports — it is meant
    // to run as a global script (that's how the deployed app vendors it). Eval its source in the
    // global scope so those vars land on window, then hand them to consumers via L.__PruneCluster*.
    if (!window.PruneClusterForLeaflet) {
      const code = (await import('prunecluster/dist/PruneCluster.js?raw')).default
      // indirect eval → runs in global scope, so top-level `var X` becomes window.X
      // eslint-disable-next-line no-eval
      ;(0, eval)(code)
    }
    // Keep the per-cluster markers list so a cluster's donut can be coloured by group
    // (GetClusterMarkers() returns undefined unless this is enabled).
    if (window.PruneCluster && window.PruneCluster.Cluster) window.PruneCluster.Cluster.ENABLE_MARKERS_LIST = true
    L.__PruneCluster = window.PruneCluster
    L.__PruneClusterForLeaflet = window.PruneClusterForLeaflet
    return L
  })().catch((err) => {
    // Do NOT cache a rejected promise — a transient failure (offline, chunk hiccup) must not
    // permanently disable the engine; the next mount should be able to retry.
    ready = null
    throw err
  })
  return ready
}

export { L }
export default loadLeafletStack
